<template>
    <div>
        <div
            v-for="(complexBracket, index) in store.filteredComplexBrackets"
            :key="complexBracket.parameter_id"
            class="box is-condensed has-no-glow-effect bracket-card"
            :class="{ 'is-selected': store.bulkSelectionEnabled && store.selectedParameterIds[complexBracket.parameter_id] }"
            data-testid="bracket-box"
        >
            <div class="bracket-card-title is-flex is-align-items-center">
                <Checkbox2
                    v-if="store.bulkSelectionEnabled"
                    v-model="store.selectedParameterIds[complexBracket.parameter_id]"
                    class="mr-2"
                />
                <span class="has-text-grey-lighter is-size-7 mr-2">#{{ index + 1 }}</span>
                <a :href="urls.eventAdmin().showBracketPage(eventId, complexBracket.parameter_id)">
                    {{ complexBracket.name }}
                </a>
            </div>

            <div class="bracket-card-meta is-size-7 is-size-6-desktop">
                <span class="bracket-card-count">
                    <span class="has-text-grey-light">{{ translate("common.total_competitors") }}: </span>
                    <span class="has-text-white">{{ complexBracket.total_real_competitors }}</span>
                </span>
                <span class="bracket-card-type">
                    <span class="has-text-grey-light">{{ translate("admin_bracket_listing.bracket_type") }}: </span>
                    <span class="has-text-white">{{ translateBracketType(complexBracket) }}</span>
                </span>
            </div>

            <div class="bracket-card-tags tags">
                <span v-if="complexBracket.isOutdated" class="tag is-danger">
                    {{ translate("common.bracket_outdated") }}
                </span>
                <span v-if="complexBracket.disqualifiedCompetitors.length > 0" class="tag is-warning">
                    {{ translate("admin_show_bracket.total_disqualifications", false, [complexBracket.disqualifiedCompetitors.length]) }}
                </span>
                <span v-if="complexBracket.typeManuallyChosen" class="tag is-info">
                    {{ translate("admin_bracket_listing.bracket_type_manually_set") }}
                </span>
            </div>

            <div class="bracket-card-actions buttons are-small is-flex-wrap-nowrap">
                <a
                    v-if="isBracketDownloadable(complexBracket.bracketType)"
                    class="button"
                    data-testid="bracket-download"
                    :href="urls.eventAdmin().downloadBracketPage(eventId, complexBracket.parameter_id)"
                    :title="translate('common.download')"
                    :aria-label="translate('common.download')"
                >
                    <span class="icon">
                        <i class="fa fa-download"></i>
                    </span>
                </a>
                <button
                    v-else
                    class="button"
                    data-testid="bracket-download"
                    disabled
                    :title="translate('common.download')"
                    :aria-label="translate('common.download')"
                >
                    <span class="icon">
                        <i class="fa fa-download"></i>
                    </span>
                </button>

                <button
                    class="button is-danger"
                    data-testid="bracket-delete"
                    :title="translate('common.delete')"
                    :aria-label="translate('common.delete')"
                    @click="deleteBracket(complexBracket.parameter_id)"
                >
                    <span class="icon">
                        <i class="fa fa-trash"></i>
                    </span>
                </button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { translate } from "../../Common/translations";
    import Checkbox2 from "../../Common/Checkbox2.vue";
    import urls from "../../Urls/urls";
    import constants from "../../Common/constants";
    import bracketsApi from "../../Api/brackets";
    import { handleError } from "../../Common/errorHandler";
    import { useBracketsListingStore } from "./bracketsListingApp";

    const props = defineProps<{
        eventId: number;
    }>();

    const store = useBracketsListingStore();

    const translateBracketType = complexBracket => {
        const bracketTranslations = constants.get("BRACKET_TYPE_TRANSLATIONS");
        return translate(bracketTranslations[complexBracket.bracketType]);
    };

    const isBracketDownloadable = bracketType => {
        return [1, 2, 3].indexOf(bracketType) !== -1;
    };

    const deleteBracket = parameterId => {
        if (!confirm(translate("common.are_you_sure"))) {
            return;
        }

        return bracketsApi
            .deleteBracket(props.eventId, parameterId)
            .then(() => {
                return store.refreshBracketsListing();
            })
            .catch(handleError);
    };
</script>

<style scoped></style>
